import React, { useState } from "react";
import { Download } from "@dreamhub/designsystem/icons";
import { buttonVariants } from "@/components/ui/button-variants";
import IconContainer from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";

interface DownloadLinkProps {
  attachmentId: string;
  children: React.ReactNode;
  className?: string;
  /** The original href to preserve for copy-link functionality */
  href?: string;
  /**
   * Fetches and saves the file. Without it the link navigates to its href,
   * which the app's download page serves.
   */
  onDownload?: (attachmentId: string) => Promise<void>;
}

/**
 * A download button (secondary button styling + Download icon + label) that triggers a
 * direct file download when clicked. Used for download links in chat messages to avoid
 * navigating to a separate page. Stays an anchor so the original href can still be copied.
 */
const DownloadLink: React.FC<DownloadLinkProps> = ({
  attachmentId,
  children,
  className,
  href,
  onDownload
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onDownload) return;

    e.preventDefault();
    e.stopPropagation();

    if (isDownloading) return;

    setIsDownloading(true);
    try {
      await onDownload(attachmentId);
    } catch {
      // Reporting a failed download is the caller's job; this only resets the spinner.
    } finally {
      setIsDownloading(false);
    }
  };

  // Build the full download URL for copy-link functionality
  const downloadUrl =
    href || `${window.location.origin}/downloads/${attachmentId}`;

  return (
    <a
      href={downloadUrl}
      onClick={handleClick}
      data-test="download-link"
      data-disabled="false"
      className={cn(
        buttonVariants({ variant: "secondary", size: "lg" }),
        "h-8 my-1 no-underline cursor-pointer",
        isDownloading && "opacity-70 cursor-wait",
        className
      )}
    >
      <IconContainer as="span" size="sm">
        <Download />
      </IconContainer>
      {children}
      {isDownloading && (
        <span className="inline-block ml-1 w-3 h-3 border border-current border-t-transparent rounded-full animate-spin align-middle" />
      )}
    </a>
  );
};

export default DownloadLink;
